import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Package, Thermometer, CheckCircle, Clock,
  RefreshCw, Camera, Truck, MapPin, Check,
  PackageCheck, LogOut, CameraOff, QrCode, Scan,
  Bell, BellOff, X, Loader2, Battery, Wifi, Layers
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { Html5Qrcode } from 'html5-qrcode';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker icon
const shipmentIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const ReceiverApp = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScannerDialog, setShowScannerDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showSubDialog, setShowSubDialog] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [cameraError, setCameraError] = useState(null);
  const [subPrefs, setSubPrefs] = useState({ alerts_temperature: true, alerts_eta: true, alerts_shock: true });
  const html5QrCodeRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };
  const [routeData, setRouteData] = useState({});

  const [myDevices, setMyDevices] = useState([]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [shipRes, subRes, devRes] = await Promise.all([
        axios.get(`${API}/shipments`, { headers }),
        axios.get(`${API}/subscriptions`, { headers }),
        axios.get(`${API}/devices`, { headers }),
      ]);
      setShipments(shipRes.data);
      setSubscriptions(subRes.data);
      setMyDevices(devRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Fetch routes for active shipments
  useEffect(() => {
    const fetchRoutes = async () => {
      const active = shipments.filter(s => ['activated', 'in_transit', 'geofence_entered'].includes(s.status));
      const routes = {};
      for (const s of active.slice(0, 10)) {
        try {
          const res = await axios.get(`${API}/shipments/${s.id}/route`, { headers });
          if (res.data?.route_points?.length >= 2) {
            routes[s.id] = res.data.route_points;
          }
        } catch { /* skip */ }
      }
      setRouteData(routes);
    };
    if (shipments.length > 0) fetchRoutes();
  }, [shipments]);

  const getSubForShipment = (shipmentId) => subscriptions.find(s => s.shipment_id === shipmentId);

  const handleSubscribe = async () => {
    if (!selectedShipment) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/subscriptions`, {
        sscc: selectedShipment.sscc,
        ...subPrefs,
      }, { headers });
      toast.success('Subscribed to alerts');
      setShowSubDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleUnsubscribe = async (subId) => {
    try {
      await axios.delete(`${API}/subscriptions/${subId}`, { headers });
      toast.success('Unsubscribed');
      fetchData();
    } catch {
      toast.error('Failed to unsubscribe');
    }
  };

  // Scanner logic
  const startScanner = async () => {
    try {
      setCameraError(null);
      setScannerActive(true);
      await new Promise(resolve => setTimeout(resolve, 100));
      html5QrCodeRef.current = new Html5Qrcode('receiver-qr-reader');
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => handleQrScanned(decodedText),
        () => {}
      );
    } catch (err) {
      setCameraError(err.message || 'Unable to access camera');
      setScannerActive(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current?.isScanning) {
      try { await html5QrCodeRef.current.stop(); } catch {}
    }
    setScannerActive(false);
    setCameraError(null);
  };

  useEffect(() => {
    return () => { if (html5QrCodeRef.current?.isScanning) html5QrCodeRef.current.stop().catch(() => {}); };
  }, []);

  const handleQrScanned = async (qrData) => {
    await stopScanner();
    let sscc = qrData;
    if (qrData.includes('sscc/')) sscc = qrData.split('sscc/')[1]?.split('?')[0];
    else if (qrData.includes('sscc=')) sscc = qrData.split('sscc=')[1]?.split('&')[0];
    const shipment = shipments.find(s => s.sscc === sscc || s.sscc?.endsWith(sscc) || s.sscc?.includes(sscc));
    if (shipment) {
      toast.success(`Found shipment: ${shipment.sscc?.slice(-8)}`);
      setSelectedShipment(shipment);
      setShowScannerDialog(false);
      setShowCompleteDialog(true);
    } else {
      toast.error('Shipment not found for this QR code');
    }
  };

  const handleCompleteShipment = async () => {
    if (!selectedShipment) return;
    setCompleting(true);
    try {
      await axios.post(`${API}/shipments/${selectedShipment.id}/complete`, {
        completion_method: 'manual_qr',
        notes: completionNotes,
      }, { headers });
      toast.success('Shipment completed successfully!');
      setShowCompleteDialog(false);
      setSelectedShipment(null);
      setCompletionNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete shipment');
    } finally {
      setCompleting(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_transit': return 'bg-blue-100 text-blue-700';
      case 'geofence_entered': return 'bg-yellow-100 text-yellow-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'activated': return 'bg-cyan-100 text-cyan-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const incomingShipments = shipments.filter(s => ['in_transit', 'geofence_entered', 'activated'].includes(s.status));
  const completedShipments = shipments.filter(s => s.status === 'completed');
  const mappableShipments = incomingShipments.filter(s => s.current_latitude && s.current_longitude);

  // Compute map bounds
  const mappableDevicesList = myDevices.filter(d => d.last_latitude && d.last_longitude);
  const mapCenter = mappableShipments.length > 0
    ? [mappableShipments[0].current_latitude, mappableShipments[0].current_longitude]
    : mappableDevicesList.length > 0
      ? [mappableDevicesList[0].last_latitude, mappableDevicesList[0].last_longitude]
      : [60.39, 5.32]; // Default: Bergen, Norway

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 p-4 md:p-8">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-testid="receiver-portal-title">Receiver Portal</h1>
            <p className="text-gray-600 mt-1">Track, subscribe, and complete incoming shipments</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-purple-500 text-white px-3 py-1">{user?.organization_name || 'Receiver'}</Badge>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="receiver-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Live Map */}
      <div className="max-w-5xl mx-auto mb-6">
        <Card className="bg-white/70 backdrop-blur-md border-white/30 shadow-lg overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-blue-500" />
              Live Shipment Map
              {mappableShipments.length > 0 && (
                <Badge className="bg-green-100 text-green-700 ml-2">{mappableShipments.length} shipments</Badge>
              )}
              {mappableDevicesList.length > 0 && (
                <Badge className="bg-indigo-100 text-indigo-700 ml-2">{mappableDevicesList.length} devices</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div style={{ height: '320px' }} data-testid="receiver-live-map">
              <MapContainer
                center={mapCenter}
                zoom={mappableShipments.length > 0 || mappableDevicesList.length > 0 ? 6 : 5}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                />
                {/* Shipment markers */}
                {mappableShipments.map(s => (
                  <Marker key={s.id} position={[s.current_latitude, s.current_longitude]} icon={shipmentIcon}>
                    <Popup>
                      <div className="text-sm">
                        <p className="font-bold">{s.sscc?.slice(-12)}</p>
                        <p>{s.origin} → {s.destination}</p>
                        <p className="text-xs text-gray-500">Status: {s.status?.replace('_', ' ')}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {/* Assigned device markers (shown when no active shipment on device) */}
                {myDevices.filter(d => d.last_latitude && d.last_longitude).map(d => (
                  <Marker key={`dev-${d.device_id}`} position={[d.last_latitude, d.last_longitude]} icon={L.divIcon({
                    className: 'custom-marker',
                    html: `<div style="width:28px;height:28px;border-radius:50%;background:${d.current_shipment_id ? '#3b82f6' : '#6366f1'};border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;">
                      <span style="color:white;font-size:10px;font-weight:bold;">${d.last_temperature != null ? Math.round(d.last_temperature) + '°' : '?'}</span>
                    </div>`,
                    iconSize: [28, 28],
                    iconAnchor: [14, 14],
                  })}>
                    <Popup>
                      <div className="text-sm">
                        <p className="font-bold">{d.name}</p>
                        {d.last_temperature != null && <p>Temp: {d.last_temperature.toFixed(1)}°C</p>}
                        {d.battery_level != null && <p>Battery: {Math.round(d.battery_level)}%</p>}
                        <p className="text-xs text-gray-500">{d.current_shipment_id ? 'In shipment' : 'Idle'}</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {/* Route polylines for shipments */}
                {Object.entries(routeData).map(([shipmentId, points]) => (
                  <Polyline
                    key={`route-${shipmentId}`}
                    positions={points.map(p => [p.lat, p.lon])}
                    pathOptions={{ color: '#6366f1', weight: 2, opacity: 0.6, dashArray: '6 4' }}
                  />
                ))}
              </MapContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions Row */}
      <div className="max-w-5xl mx-auto mb-6 grid grid-cols-4 gap-4">
        <Card className="bg-white/60 backdrop-blur-md border-white/30">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Truck className="w-5 h-5 text-blue-500" />
              <div className="text-3xl font-bold text-blue-600">{incomingShipments.length}</div>
            </div>
            <div className="text-sm text-gray-600">Incoming</div>
          </CardContent>
        </Card>
        <Card className="bg-white/60 backdrop-blur-md border-white/30">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Layers className="w-5 h-5 text-indigo-500" />
              <div className="text-3xl font-bold text-indigo-600">{myDevices.length}</div>
            </div>
            <div className="text-sm text-gray-600">My Devices</div>
          </CardContent>
        </Card>
        <Card className="bg-white/60 backdrop-blur-md border-white/30 cursor-pointer hover:border-purple-300 transition-all"
              onClick={() => setShowScannerDialog(true)} data-testid="scan-to-complete-card">
          <CardContent className="p-4 text-center">
            <Scan className="w-6 h-6 text-purple-500 mx-auto mb-1" />
            <div className="text-sm font-medium text-purple-700">Scan to Complete</div>
          </CardContent>
        </Card>
        <Card className="bg-white/60 backdrop-blur-md border-white/30">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div className="text-3xl font-bold text-green-600">{completedShipments.length}</div>
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </CardContent>
        </Card>
      </div>

      {/* Incoming Shipments with Subscribe */}
      <div className="max-w-5xl mx-auto mb-6">
        <Card className="bg-white/70 backdrop-blur-md border-white/30 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-500" />
                Incoming Shipments
              </CardTitle>
              <CardDescription>Shipments on their way to you — subscribe for live alerts</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-shipments-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
            ) : incomingShipments.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No incoming shipments</p>
              </div>
            ) : (
              <div className="space-y-3">
                {incomingShipments.map((shipment) => {
                  const sub = getSubForShipment(shipment.id);
                  return (
                    <div key={shipment.id} className="p-4 bg-white/50 rounded-xl border border-gray-100" data-testid={`shipment-card-${shipment.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Truck className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-mono text-sm font-medium">{shipment.sscc?.slice(-12)}</div>
                            <div className="text-xs text-gray-500">{shipment.origin} → {shipment.destination}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {sub ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              onClick={() => handleUnsubscribe(sub.id)}
                              data-testid={`unsub-btn-${shipment.id}`}
                            >
                              <Bell className="w-4 h-4 mr-1" /> Subscribed
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedShipment(shipment);
                                setSubPrefs({ alerts_temperature: true, alerts_eta: true, alerts_shock: true });
                                setShowSubDialog(true);
                              }}
                              data-testid={`sub-btn-${shipment.id}`}
                            >
                              <BellOff className="w-4 h-4 mr-1" /> Subscribe
                            </Button>
                          )}
                          <Badge className={getStatusColor(shipment.status)}>
                            {shipment.status?.replace('_', ' ')}
                          </Badge>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => { setSelectedShipment(shipment); setShowCompleteDialog(true); }}
                            data-testid={`complete-btn-${shipment.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Complete
                          </Button>
                        </div>
                      </div>
                      {/* Show location if available */}
                      {shipment.current_latitude && shipment.current_longitude && (
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                          <MapPin className="w-3 h-3" />
                          {shipment.current_latitude.toFixed(4)}, {shipment.current_longitude.toFixed(4)}
                          {sub && (
                            <span className="ml-auto flex items-center gap-1 text-green-600">
                              <Bell className="w-3 h-3" />
                              {[sub.alerts_temperature && 'Temp', sub.alerts_eta && 'ETA', sub.alerts_shock && 'Shock'].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* My Assigned Devices */}
      {myDevices.length > 0 && (
        <div className="max-w-5xl mx-auto mb-6">
          <Card className="bg-white/70 backdrop-blur-md border-white/30 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-500" />
                My Devices ({myDevices.length})
              </CardTitle>
              <CardDescription>Trackers assigned to your organization</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {myDevices.map((device) => {
                  const hasShipment = !!device.current_shipment_id;
                  const temp = device.last_temperature;
                  const battery = device.battery_level;
                  const lastSeen = device.last_seen ? new Date(device.last_seen) : null;
                  const isRecent = lastSeen && (Date.now() - lastSeen.getTime()) < 3600000;
                  return (
                    <div key={device.device_id} className="p-3 rounded-lg border bg-white/80" data-testid={`device-card-${device.device_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm text-slate-900">{device.name}</span>
                        {hasShipment ? (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">In Use</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 text-xs">Idle</Badge>
                        )}
                      </div>
                      <div className="flex gap-3 text-xs text-slate-500">
                        {temp != null && (
                          <span className="flex items-center gap-1">
                            <Thermometer className="w-3 h-3 text-blue-500" />
                            {temp.toFixed(1)}°C
                          </span>
                        )}
                        {battery != null && (
                          <span className="flex items-center gap-1">
                            <Battery className="w-3 h-3 text-green-500" />
                            {Math.round(battery)}%
                          </span>
                        )}
                        {lastSeen && (
                          <span className="flex items-center gap-1">
                            <Wifi className={`w-3 h-3 ${isRecent ? 'text-green-500' : 'text-slate-300'}`} />
                            {lastSeen.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      {device.short_code && (
                        <div className="mt-1 text-xs font-mono text-slate-400">Code: {device.short_code}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Completed Shipments */}
      {completedShipments.length > 0 && (
        <div className="max-w-5xl mx-auto">
          <Card className="bg-white/70 backdrop-blur-md border-white/30 shadow-lg">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <PackageCheck className="w-5 h-5 text-green-500" />
                Completed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {completedShipments.slice(0, 5).map((shipment) => (
                  <div key={shipment.id} className="p-3 bg-green-50/50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <div className="font-mono text-sm">{shipment.sscc?.slice(-12)}</div>
                        <div className="text-xs text-gray-500">{shipment.origin}</div>
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700">Completed</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Subscribe Dialog */}
      <Dialog open={showSubDialog} onOpenChange={setShowSubDialog}>
        <DialogContent className="max-w-sm" data-testid="subscribe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              Subscribe to Alerts
            </DialogTitle>
            <DialogDescription>
              Get notified about this shipment ({selectedShipment?.sscc?.slice(-12)})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Thermometer className="w-4 h-4 text-red-500" /> Temperature Alerts
              </Label>
              <Switch
                checked={subPrefs.alerts_temperature}
                onCheckedChange={v => setSubPrefs({ ...subPrefs, alerts_temperature: v })}
                data-testid="sub-temp-toggle"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-500" /> ETA Alerts
              </Label>
              <Switch
                checked={subPrefs.alerts_eta}
                onCheckedChange={v => setSubPrefs({ ...subPrefs, alerts_eta: v })}
                data-testid="sub-eta-toggle"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Package className="w-4 h-4 text-purple-500" /> Shock/Impact Alerts
              </Label>
              <Switch
                checked={subPrefs.alerts_shock}
                onCheckedChange={v => setSubPrefs({ ...subPrefs, alerts_shock: v })}
                data-testid="sub-shock-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubDialog(false)}>Cancel</Button>
            <Button onClick={handleSubscribe} disabled={subscribing} data-testid="subscribe-submit-btn">
              {subscribing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
              Subscribe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Scanner Dialog */}
      <Dialog open={showScannerDialog} onOpenChange={setShowScannerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Scan to Complete Shipment
            </DialogTitle>
            <DialogDescription>Scan the pallet QR code to mark the shipment as received</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {cameraError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <CameraOff className="w-4 h-4" />{cameraError}
              </div>
            )}
            {!scannerActive ? (
              <div className="flex flex-col items-center gap-4">
                <div className="w-48 h-48 bg-gray-100 rounded-2xl flex items-center justify-center">
                  <QrCode className="w-24 h-24 text-gray-400" />
                </div>
                <Button onClick={startScanner} className="bg-purple-600 hover:bg-purple-700">
                  <Camera className="w-4 h-4 mr-2" /> Start Camera
                </Button>
              </div>
            ) : (
              <div className="w-full">
                <div id="receiver-qr-reader" className="w-full rounded-xl overflow-hidden"></div>
                <Button variant="outline" className="w-full mt-4" onClick={stopScanner}>Stop Camera</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Shipment Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-green-600" />
              Complete Shipment
            </DialogTitle>
            <DialogDescription>Confirm receipt and complete this shipment</DialogDescription>
          </DialogHeader>
          {selectedShipment && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">SSCC</div>
                    <div className="font-mono font-medium">{selectedShipment.sscc?.slice(-12)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Origin</div>
                    <div className="font-medium">{selectedShipment.origin}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Device</div>
                    <div className="font-medium">{selectedShipment.device_id}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Status</div>
                    <Badge className={getStatusColor(selectedShipment.status)}>{selectedShipment.status?.replace('_', ' ')}</Badge>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Add any notes about the condition..."
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
            <Button onClick={handleCompleteShipment} disabled={completing} className="bg-green-600 hover:bg-green-700" data-testid="confirm-complete-btn">
              {completing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Confirm Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiverApp;
